'use client';

import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { useRefreshPmsContactTypes } from '@/lib/services/integrations/pms/hooks';
// Imported directly, not via the `pms` barrel — several suites mock that barrel with a partial
// factory, which resolves this registry to `undefined` at module-init time. See its own header.
import { getPmsProviderPresentationFor } from '@/lib/services/integrations/pms/provider-presentation';
import type { PmsProvider } from '@/lib/services/integrations/pms/types';

/**
 * **Pull the firm's participant types from the PMS now**, rather than waiting for the nightly sweep.
 *
 * ## Why it lives beside the picker rather than on the integration card (VALE-587)
 *
 * It was introduced on the ⋮ menu of the Account → Integrations card (VALE-152), which put it two
 * navigations away from the only place its result is visible — and behind an **admin-only page wall**
 * (`isAdministrator`, `account/integrations/page.tsx`), so the fee-earner who had just added a
 * participant type in ActionStep could not reach it at all. The catalogue is only ever *read* on the
 * contact card, so that is where the affordance for re-reading it belongs. The endpoint carries no
 * role gate of its own — `LawFirmAuthGuard` alone — so moving it costs no new permission.
 *
 * ## One definition of the action, deliberately
 *
 * Both selector surfaces mount this same component — the contact form's `PmsContactTypeSelector` and
 * the Contacts drawer's attribute section — rather than each wiring `useRefreshPmsContactTypes` and
 * writing its own toasts. The two would otherwise describe the same refresh differently the first
 * time either was touched.
 *
 * **Reports the outcome either way, and the success toast carries the count.** A refresh that only
 * said "done" would leave the reader no better informed than before they clicked — the number is what
 * tells them whether the type they just added in the PMS actually arrived. A failure surfaces the
 * provider's own message; nothing was written, so the list they are looking at is still what is
 * stored.
 *
 * **Render it only for a provider that publishes a per-firm catalogue** — callers decide that from
 * the catalogue itself (`publishesContactTypeCatalogue`), never from a provider name, so a third
 * provider that publishes types needs no change here and Smokeball never receives it.
 */
export function PmsContactTypeRefreshButton({
  provider,
  className
}: {
  provider: PmsProvider;
  className?: string;
}) {
  const refresh = useRefreshPmsContactTypes(provider);
  const presentation = getPmsProviderPresentationFor(provider);

  const handleRefresh = async (): Promise<void> => {
    try {
      const { contactTypes } = await refresh.mutateAsync();

      toast.success('Participant types refreshed', {
        description: `${contactTypes.length} participant ${
          contactTypes.length === 1 ? 'type' : 'types'
        } are now available when filing a contact.`
      });
    } catch (error) {
      toast.error('Refresh failed', {
        description:
          error instanceof Error
            ? error.message
            : 'Unable to reach the practice management system'
      });
    }
  };

  const label = refresh.isPending
    ? 'Refreshing participant types…'
    : `Refresh participant types from ${presentation.label}`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            /*
              `type="button"` is load-bearing, not boilerplate: this renders inside `ContactForm`,
              where a bare button defaults to `submit` and a refresh click would commit the whole
              contact.
            */
            type="button"
            variant="ghost"
            size="icon-xs"
            /*
              Cleared while in flight, not merely `disabled` — this is an upstream read against the
              firm's PMS, and a second one should not be one missing class away. Same reasoning the
              menu item this replaces carried.
            */
            onClick={refresh.isPending ? undefined : handleRefresh}
            disabled={refresh.isPending}
            aria-label={label}
            data-testid="pms-contact-type-refresh"
            className={cn('text-muted-foreground shrink-0', className)}
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', refresh.isPending && 'animate-spin')}
              aria-hidden
            />
          </Button>
        </TooltipTrigger>

        <TooltipContent side="bottom" className="max-w-xs">
          {/*
            `font-normal normal-case tracking-normal` is load-bearing, not
            defensive tidying. `TooltipContent` is deliberately NOT portalled
            (see `components/ui/tooltip.tsx`), so it renders in place — and this
            button's `AttributeSection` call site puts it in the `titleAction`
            slot *inside* the section `<h3>`, whose `ATTRIBUTE_GROUP_TITLE`
            carries `font-semibold tracking-wide uppercase`. All three inherit
            straight into the tooltip, so a sentence of prose rendered as
            BOLD SPACED-OUT CAPITALS. `text-xs` sets only the size and overrides
            none of them.
          */}
          <p className="text-xs font-normal tracking-normal normal-case">
            {refresh.isPending
              ? label
              : `Re-read this firm's participant types from ${presentation.label} now, instead of waiting for the nightly sync.`}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
